import type { DocumentAnalysisResult, DocumentType } from '@/types/health'

export type DocumentAnalysisConfidenceLabel = 'high' | 'medium' | 'low'

export interface DocumentAnalysisMetadata {
  inferred_specialty: string
  summary_short: string
  timeline_summary: string
  analysis_confidence_score: number
  analysis_confidence_label: DocumentAnalysisConfidenceLabel
  analysis_missing_fields: string[]
  analysis_review_required: boolean
  abnormal_analysis_count: number
  extracted_counts: {
    analyses: number
    prescriptions: number
    diagnostics: number
  }
  clinical_focuses: string[]
}

const DOCUMENT_TYPE_SPECIALTY_LABELS: Record<DocumentType, string> = {
  blood_test: 'Biologie',
  radiology: 'Imagerie',
  prescription: 'Traitement',
  consultation_report: 'Consultation',
  discharge_summary: 'Hospitalisation',
  pathology: 'Anatomopathologie',
  cardiology: 'Cardiologie',
  specialist_report: 'Consultation',
  vaccination_record: 'Vaccination',
  insurance_document: 'Administratif',
  other: 'Autre'
}

function normalizeText(value: string | null | undefined): string {
  return (value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
}

function uniqueItems(values: Array<string | null | undefined>): string[] {
  return [...new Set(values.map((value) => value?.trim()).filter(Boolean) as string[])]
}

function truncateText(value: string, maxLength = 120): string {
  if (value.length <= maxLength) {
    return value
  }

  return `${value.slice(0, maxLength).trimEnd()}...`
}

function countAbnormalAnalyses(analysisResult: DocumentAnalysisResult): number {
  return analysisResult.analyses.filter((entry) =>
    entry.statut === 'anormal' || entry.statut === 'critique' || entry.statut === 'limite'
  ).length
}

function pickPriorityAnalyses(analysisResult: DocumentAnalysisResult): string[] {
  const abnormalFirst = [
    ...analysisResult.analyses.filter((entry) =>
      entry.statut === 'critique' || entry.statut === 'anormal' || entry.statut === 'limite'
    ),
    ...analysisResult.analyses.filter((entry) => !entry.statut || entry.statut === 'normal')
  ]

  return uniqueItems(abnormalFirst.map((entry) => entry.nom_analyse)).slice(0, 3)
}

function detectClinicalFocuses(analysisResult: DocumentAnalysisResult): string[] {
  const haystack = normalizeText([
    analysisResult.resume,
    analysisResult.recommandations,
    analysisResult.medecin,
    analysisResult.laboratoire,
    ...analysisResult.diagnostics.map((entry) => entry.diagnostic),
    ...analysisResult.analyses.map((entry) => entry.nom_analyse)
  ].filter(Boolean).join(' '))

  return uniqueItems([
    /crp|calprotectine|albumine|rch|colite|digest|intestin|rectorrag/.test(haystack)
      ? 'Inflammation digestive'
      : null,
    /ferritine|hemoglobine|hematocrite|vgm|tcmh|ccmh|transferrine|fer serique/.test(haystack)
      ? 'Fer / hematologie'
      : null,
    /tsh|t4 libre|t3 libre|thyroid/.test(haystack)
      ? 'Thyroide'
      : null,
    /fsh|lh|estradiol|progesterone|endometr|gyne/.test(haystack)
      ? 'Gynecologie / hormones'
      : null,
    /cholesterol|ldl|hdl|triglycerides/.test(haystack)
      ? 'Cardiometabolique'
      : null,
    /vitamine d|vitamine b12|folates|carence/.test(haystack)
      ? 'Carences'
      : null
  ])
}

function inferSpecialtyFromClinicalFocuses(
  analysisResult: DocumentAnalysisResult,
  focuses: string[]
): string {
  const normalizedFocuses = focuses.map((focus) => normalizeText(focus))
  const summaryHaystack = normalizeText([
    analysisResult.resume,
    analysisResult.recommandations,
    ...analysisResult.diagnostics.map((entry) => entry.diagnostic)
  ].filter(Boolean).join(' '))

  if (normalizedFocuses.some((focus) => focus.includes('inflammation digestive')) || /\brch\b|digest|intestin/.test(summaryHaystack)) {
    return 'Gastroenterologie'
  }

  if (normalizedFocuses.some((focus) => focus.includes('gynecologie')) || /endometr|gyne/.test(summaryHaystack)) {
    return 'Gynecologie'
  }

  if (normalizedFocuses.some((focus) => focus.includes('thyroide'))) {
    return 'Endocrinologie'
  }

  if (normalizedFocuses.some((focus) => focus.includes('hematologie'))) {
    return analysisResult.type_document === 'blood_test' ? 'Biologie' : 'Hematologie'
  }

  if (normalizedFocuses.some((focus) => focus.includes('cardiometabolique'))) {
    return 'Cardiologie'
  }

  return DOCUMENT_TYPE_SPECIALTY_LABELS[analysisResult.type_document] || 'Autre'
}

function buildSummaryForBloodTest(analysisResult: DocumentAnalysisResult): string {
  const analysesCount = analysisResult.analyses.length
  const abnormalCount = countAbnormalAnalyses(analysisResult)
  const priorityAnalyses = pickPriorityAnalyses(analysisResult)

  if (analysesCount === 0) {
    return 'Bilan biologique importe, extraction a verifier.'
  }

  if (abnormalCount > 0) {
    return truncateText(
      `${analysesCount} mesure${analysesCount > 1 ? 's' : ''} - ${abnormalCount} a surveiller${priorityAnalyses.length > 0 ? `: ${priorityAnalyses.join(', ')}` : ''}`,
      125
    )
  }

  return truncateText(
    `${analysesCount} mesure${analysesCount > 1 ? 's' : ''} biologique${analysesCount > 1 ? 's' : ''}${priorityAnalyses.length > 0 ? `: ${priorityAnalyses.join(', ')}` : ''}`,
    125
  )
}

function buildSummaryForPrescription(analysisResult: DocumentAnalysisResult): string {
  const names = uniqueItems(analysisResult.prescriptions.map((entry) => entry.medicament)).slice(0, 3)

  if (names.length === 0) {
    return truncateText(analysisResult.resume || 'Ordonnance importee.', 125)
  }

  return truncateText(
    `${analysisResult.prescriptions.length} traitement${analysisResult.prescriptions.length > 1 ? 's' : ''} detecte${analysisResult.prescriptions.length > 1 ? 's' : ''}: ${names.join(', ')}`,
    125
  )
}

function buildSummaryForDiagnostic(analysisResult: DocumentAnalysisResult): string {
  const diagnoses = uniqueItems(analysisResult.diagnostics.map((entry) => entry.diagnostic)).slice(0, 2)

  if (diagnoses.length > 0) {
    return truncateText(diagnoses.join(' - '), 125)
  }

  return truncateText(analysisResult.resume || 'Compte-rendu medical importe.', 125)
}

function buildSummaryShort(analysisResult: DocumentAnalysisResult): string {
  switch (analysisResult.type_document) {
    case 'blood_test':
      return buildSummaryForBloodTest(analysisResult)
    case 'prescription':
      return buildSummaryForPrescription(analysisResult)
    case 'consultation_report':
    case 'discharge_summary':
    case 'specialist_report':
    case 'cardiology':
    case 'pathology':
      return buildSummaryForDiagnostic(analysisResult)
    default:
      return truncateText(analysisResult.resume || 'Document medical analyse.', 125)
  }
}

function buildTimelineSummary(analysisResult: DocumentAnalysisResult, summaryShort: string): string {
  if (analysisResult.type_document === 'blood_test') {
    const abnormalCount = countAbnormalAnalyses(analysisResult)
    const analysesCount = analysisResult.analyses.length
    return abnormalCount > 0
      ? `${analysesCount} mesure${analysesCount > 1 ? 's' : ''}, ${abnormalCount} a surveiller`
      : `${analysesCount} mesure${analysesCount > 1 ? 's' : ''} biologique${analysesCount > 1 ? 's' : ''}`
  }

  if (analysisResult.type_document === 'prescription') {
    return `${analysisResult.prescriptions.length} prescription${analysisResult.prescriptions.length > 1 ? 's' : ''} detectee${analysisResult.prescriptions.length > 1 ? 's' : ''}`
  }

  return summaryShort
}

function buildMissingFields(analysisResult: DocumentAnalysisResult, summaryShort: string): string[] {
  const missing = uniqueItems([
    analysisResult.date_document ? null : 'date_document',
    analysisResult.medecin ? null : 'medecin',
    analysisResult.resume || summaryShort ? null : 'resume'
  ])

  if (analysisResult.type_document === 'blood_test') {
    return uniqueItems([
      ...missing,
      analysisResult.date_prelevement ? null : 'date_prelevement',
      analysisResult.laboratoire ? null : 'laboratoire',
      analysisResult.analyses.length > 0 ? null : 'analyses'
    ])
  }

  if (analysisResult.type_document === 'prescription') {
    return uniqueItems([
      ...missing,
      analysisResult.prescriptions.length > 0 ? null : 'prescriptions'
    ])
  }

  if (
    analysisResult.type_document === 'consultation_report' ||
    analysisResult.type_document === 'specialist_report' ||
    analysisResult.type_document === 'discharge_summary'
  ) {
    return uniqueItems([
      ...missing,
      analysisResult.diagnostics.length > 0 || normalizeText(summaryShort).length > 0 ? null : 'diagnostics_ou_resume'
    ])
  }

  return missing
}

function computeConfidenceScore(
  analysisResult: DocumentAnalysisResult,
  summaryShort: string,
  missingFields: string[]
): number {
  let score = 20

  if (analysisResult.type_document) score += 10
  if (analysisResult.date_document) score += 15
  if (analysisResult.medecin) score += 10
  if (analysisResult.laboratoire) score += 8
  if (analysisResult.resume || summaryShort) score += 10
  if (analysisResult.date_prelevement) score += 12
  if (analysisResult.analyses.length > 0) score += 18
  if (analysisResult.prescriptions.length > 0) score += 14
  if (analysisResult.diagnostics.length > 0) score += 12

  const fallbackMarkers = normalizeText(`${analysisResult.resume || ''} ${analysisResult.recommandations || ''}`)
  if (
    /analyse partielle|verification manuelle|sans analyse detaillee|temporairement indisponible|erreur lors de l analyse/.test(
      fallbackMarkers
    )
  ) {
    score -= 25
  }

  score -= missingFields.length * 8

  if (analysisResult.type_document === 'blood_test' && analysisResult.analyses.length >= 12) {
    score += 8
  }

  return Math.max(0, Math.min(100, score))
}

function toConfidenceLabel(score: number): DocumentAnalysisConfidenceLabel {
  if (score >= 80) {
    return 'high'
  }

  if (score >= 55) {
    return 'medium'
  }

  return 'low'
}

export function buildDocumentAnalysisMetadata(
  analysisResult: DocumentAnalysisResult
): DocumentAnalysisMetadata {
  const summaryShort = buildSummaryShort(analysisResult)
  const focuses = detectClinicalFocuses(analysisResult)
  const inferredSpecialty = inferSpecialtyFromClinicalFocuses(analysisResult, focuses)
  const missingFields = buildMissingFields(analysisResult, summaryShort)
  const confidenceScore = computeConfidenceScore(analysisResult, summaryShort, missingFields)
  const confidenceLabel = toConfidenceLabel(confidenceScore)
  const abnormalAnalysisCount = countAbnormalAnalyses(analysisResult)
  const timelineSummary = buildTimelineSummary(analysisResult, summaryShort)

  return {
    inferred_specialty: inferredSpecialty,
    summary_short: summaryShort,
    timeline_summary: timelineSummary,
    analysis_confidence_score: confidenceScore,
    analysis_confidence_label: confidenceLabel,
    analysis_missing_fields: missingFields,
    analysis_review_required:
      confidenceLabel === 'low' ||
      missingFields.length > 0 ||
      (analysisResult.type_document === 'blood_test' && analysisResult.analyses.length === 0),
    abnormal_analysis_count: abnormalAnalysisCount,
    extracted_counts: {
      analyses: analysisResult.analyses.length,
      prescriptions: analysisResult.prescriptions.length,
      diagnostics: analysisResult.diagnostics.length
    },
    clinical_focuses: focuses
  }
}
